import { ActionIcon, Badge, Box, Button, Group, Loader, Stack, Text } from '@mantine/core';
import { modals } from '@mantine/modals';
import { IconCheck, IconCopy, IconRefresh } from '@tabler/icons-react';
import { CopyToClipboard } from 'components/CopyToClipboard';
import { i18nKeys, useTranslation } from 'locales';
import { useEffect } from 'react';
import {
  type DnsRecord,
  useLoadEmailDomain,
  useRefreshEmailDomain,
  useVerifyEmailDomain,
} from '../api';

type DnsRecordRowProps = {
  record: DnsRecord;
  isChecking: boolean;
};

const DnsRecordRow = ({ record, isChecking }: DnsRecordRowProps) => {
  const { t } = useTranslation();

  return (
    <Group
      wrap="nowrap"
      gap="sm"
      p="sm"
      bdrs="md"
      bd="1px solid var(--mantine-color-gray-3)"
      align="center"
    >
      <Badge variant="light" radius="sm" tt="uppercase" style={{ flex: 'none' }}>
        {record.type}
      </Badge>
      <Box flex={1} miw={0}>
        <Text size="sm" ff="monospace" truncate>
          {record.host}
        </Text>
        <Text size="xs" c="dimmed" ff="monospace" truncate>
          {record.data}
        </Text>
      </Box>
      {isChecking ? (
        <Loader size="xs" />
      ) : (
        <Badge
          variant="light"
          radius="xl"
          color={record.valid ? 'validation' : 'gray'}
          style={{ flex: 'none' }}
          leftSection={record.valid ? <IconCheck size={12} /> : undefined}
        >
          {t(
            record.valid
              ? i18nKeys.CUSTOM_DOMAIN.RECORD_VERIFIED
              : i18nKeys.CUSTOM_DOMAIN.RECORD_PENDING,
          )}
        </Badge>
      )}
      <CopyToClipboard text={record.data} />
    </Group>
  );
};

// Reads the domain itself rather than taking it as a prop: the modal has to keep
// rendering fresh records while the user runs a verification from inside it.
export const DnsRecordsModal = () => {
  const { t } = useTranslation();

  const { emailDomain } = useLoadEmailDomain();
  const { verifyEmailDomain, isVerifyEmailDomainLoading } = useVerifyEmailDomain();
  const { refreshEmailDomain, isRefreshEmailDomainLoading } = useRefreshEmailDomain();

  // SendGrid is the source of truth for the record values, so pull them once on
  // open rather than showing whatever was stored when the domain was created.
  // biome-ignore lint/correctness/useExhaustiveDependencies: run once on mount; the mutation identity is unstable
  useEffect(() => {
    refreshEmailDomain();
  }, []);

  const records = emailDomain?.dnsRecords ?? [];
  const isChecking = isVerifyEmailDomainLoading || isRefreshEmailDomainLoading;

  const copyAllRecords = () => {
    const text = records
      .map(
        record =>
          `${record.type.toUpperCase()}\n${t(i18nKeys.CUSTOM_DOMAIN.RECORD_NAME)}: ${record.host}\n${t(i18nKeys.CUSTOM_DOMAIN.RECORD_VALUE)}: ${record.data}`,
      )
      .join('\n\n');

    navigator.clipboard.writeText(text);
  };

  return (
    <Stack gap="md">
      <Group justify="space-between" align="start" wrap="nowrap">
        <Text size="sm" c="dimmed">
          {t(i18nKeys.CUSTOM_DOMAIN.DNS_MODAL_INFO)}
        </Text>
        <ActionIcon
          variant="subtle"
          onClick={() => refreshEmailDomain()}
          loading={isRefreshEmailDomainLoading}
          aria-label={t(i18nKeys.CUSTOM_DOMAIN.REFRESH_RECORDS)}
        >
          <IconRefresh size={16} />
        </ActionIcon>
      </Group>
      <Stack gap="xs">
        {records.map(record => (
          <DnsRecordRow key={record.host} record={record} isChecking={isChecking} />
        ))}
      </Stack>
      <Button
        variant="default"
        leftSection={<IconCopy size={16} />}
        onClick={copyAllRecords}
        disabled={records.length === 0}
      >
        {t(i18nKeys.CUSTOM_DOMAIN.COPY_ALL_RECORDS)}
      </Button>
      <Group justify="end" align="center" mt="xs" wrap="nowrap">
        <Text size="xs" c="dimmed" flex={1}>
          {t(i18nKeys.CUSTOM_DOMAIN.SAVED_NOTICE)}
        </Text>
        <Button variant="default" onClick={() => modals.closeAll()}>
          {t(i18nKeys.CUSTOM_DOMAIN.LATER)}
        </Button>
        <Button onClick={() => verifyEmailDomain()} loading={isVerifyEmailDomainLoading}>
          {t(i18nKeys.CUSTOM_DOMAIN.VERIFY)}
        </Button>
      </Group>
    </Stack>
  );
};
